import { useCallback, useState } from 'react';

const ZOOM_MIN = 50;
const ZOOM_MAX = 200;
const ZOOM_STEP = 25;
const BASE_MAX_REM = 22;
const BASE_MAX_VH = 40;
const SMALL_IMG_MAX = 180;

/**
 * Картинка из Markdown-условия: компактный стартовый размер и масштаб 50–200%.
 */
export default function ResizableMarkdownImage(props) {
  const {
    node: _node,
    src,
    alt,
    title,
    className,
    style: styleProp,
    onLoad: onLoadProp,
    ...rest
  } = props;
  const [zoomPercent, setZoomPercent] = useState(100);
  const [compactToolbar, setCompactToolbar] = useState(false);

  const onLoad = useCallback(
    (e) => {
      onLoadProp?.(e);
      const { naturalWidth, naturalHeight } = e.currentTarget;
      if (naturalWidth > 0 && naturalHeight > 0) {
        setCompactToolbar(
          naturalWidth <= SMALL_IMG_MAX && naturalHeight <= SMALL_IMG_MAX,
        );
      }
    },
    [onLoadProp],
  );

  const factor = zoomPercent / 100;
  const maxWidth = `min(100%, ${BASE_MAX_REM * factor}rem)`;
  const maxHeight = `${BASE_MAX_VH * factor}vh`;

  const dec = () =>
    setZoomPercent((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  const inc = () =>
    setZoomPercent((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  const reset = () => setZoomPercent(100);

  if (!src) {
    return null;
  }

  const baseStyle =
    styleProp && typeof styleProp === 'object' ? styleProp : {};

  const img = (
    <img
      {...rest}
      src={src}
      alt={alt ?? ''}
      title={title}
      className={['markdown-statement-img__img', className].filter(Boolean).join(' ')}
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
      style={
        compactToolbar
          ? {
              ...baseStyle,
              maxWidth: 'min(100%, 11rem)',
              maxHeight: '6rem',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'inline-block',
              verticalAlign: 'middle',
            }
          : {
              ...baseStyle,
              maxWidth,
              maxHeight,
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
            }
      }
    />
  );

  if (compactToolbar) {
    return (
      <span className="markdown-statement-img markdown-statement-img--inline-small">
        {img}
      </span>
    );
  }

  return (
    <figure className="markdown-statement-img">
      <div className="markdown-statement-img__scroll">
        {img}
      </div>
      <figcaption className="markdown-statement-img__toolbar">
        <button
          type="button"
          className="btn btn-outline btn-sm markdown-statement-img__btn"
          onClick={dec}
          disabled={zoomPercent <= ZOOM_MIN}
          aria-label="Уменьшить рисунок"
        >
          −
        </button>
        <span className="markdown-statement-img__zoom-label" aria-live="polite">
          {zoomPercent}%
        </span>
        <button
          type="button"
          className="btn btn-outline btn-sm markdown-statement-img__btn"
          onClick={inc}
          disabled={zoomPercent >= ZOOM_MAX}
          aria-label="Увеличить рисунок"
        >
          +
        </button>
        <button
          type="button"
          className="btn btn-outline btn-sm markdown-statement-img__btn markdown-statement-img__btn-reset"
          onClick={reset}
          aria-label="Сбросить масштаб рисунка"
        >
          Исходный
        </button>
      </figcaption>
    </figure>
  );
}
